import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { Injectable, InjectionToken, Injector, Type, inject } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export const DIALOG_DATA = new InjectionToken<any>('DIALOG_DATA');

export interface DialogConfig<D = unknown> {
  data?: D;
  width?: string;
  maxWidth?: string;
  disableClose?: boolean;
  panelClass?: string | string[];
}

export class DialogRef<R = any> {
  private readonly closed$ = new Subject<R | undefined>();
  private closed = false;

  constructor(
    private readonly overlayRef: OverlayRef,
    private readonly onDispose: () => void,
  ) {}

  close(result?: R): void {
    if (this.closed) {
      return;
    }
    this.closed = true;
    this.overlayRef.dispose();
    this.onDispose();
    this.closed$.next(result);
    this.closed$.complete();
  }

  afterClosed(): Observable<R | undefined> {
    return this.closed$.asObservable();
  }
}

@Injectable({ providedIn: 'root' })
export class DialogService {
  private readonly overlay = inject(Overlay);
  private readonly injector = inject(Injector);
  private readonly openRefs = new Set<DialogRef>();

  open<C>(component: Type<C>, config: DialogConfig = {}): DialogRef {
    const panelClass = ['ui-dialog-panel'];
    if (config.panelClass) {
      panelClass.push(...(Array.isArray(config.panelClass) ? config.panelClass : [config.panelClass]));
    }

    const overlayRef = this.overlay.create({
      hasBackdrop: true,
      backdropClass: 'ui-dialog-backdrop',
      panelClass,
      positionStrategy: this.overlay.position().global().centerHorizontally().centerVertically(),
      scrollStrategy: this.overlay.scrollStrategies.block(),
      width: config.width ?? 'min(92vw, 34rem)',
      maxWidth: config.maxWidth ?? 'min(96vw, 40rem)',
    });

    const dialogRef = new DialogRef(overlayRef, () => {
      this.openRefs.delete(dialogRef);
    });
    this.openRefs.add(dialogRef);

    const childInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: DialogRef, useValue: dialogRef },
        { provide: DIALOG_DATA, useValue: config.data ?? null },
      ],
    });

    overlayRef.attach(new ComponentPortal(component, null, childInjector));

    if (!config.disableClose) {
      overlayRef.backdropClick().subscribe(() => dialogRef.close());
      overlayRef.keydownEvents().subscribe((event) => {
        if (event.key === 'Escape') {
          dialogRef.close();
        }
      });
    }

    return dialogRef;
  }

  closeAll(): void {
    [...this.openRefs].forEach((ref) => ref.close());
  }
}
